import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CommonDataService } from '@sb-tools/typescript/services/common-data.service';
import { ThreadFilterType } from '../../../../core/enums/thread-filter-type.enum';
import { ThreadFilterNamePipe } from '../../../../shared/pipes/thread-filter-name.pipe';
import { ThreadFilterService } from '../../../../core/services/thread-filter.service';
import { SignalRService } from '../../../../core/services/signalR.service';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { AppInsightsService } from '@sb-tools/typescript/services/app-insights.service';
import { DetailedTicketDTO } from '../../../../core/models/dto/detailed-ticket.dto';
import { ThreadFilterGuiModel } from '../../../../core/models/thread-filter-gui.model';
import { FilteredRepliesCountUpdatedEventModel } from '../../../../core/models/dto/signalR/filtered-replies-count-updated-event-model.model';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { InlineSVGComponent } from '../../../../../sb-modules/Frontend-Inline-SVG-Module/inline-svg.component';
import { FormsModule } from '@angular/forms';

@Component({
    selector: 'app-thread-filter',
    templateUrl: './thread-filter.component.html',
    styleUrls: ['./thread-filter.component.scss'],
    imports: [
        TranslateModule,
        NgClass,
        TooltipModule,
        NgTemplateOutlet,
        BsDropdownModule,
        InlineSVGComponent,
        FormsModule,
    ],
})
export class ThreadFilterComponent implements OnInit, OnDestroy {
    @Input() ticket: DetailedTicketDTO;

    searchQueryUpdate = new Subject<string>();
    threadFilters: ThreadFilterGuiModel[] = [];
    threadFilterService = ThreadFilterService;
    threadFilterTypeEnum = ThreadFilterType;

    private unsubscriber = new Subject<void>();

    constructor(
        private appInsightsService: AppInsightsService,
        private threadFilterNamePipe: ThreadFilterNamePipe,
        private commonDataService: CommonDataService,
        private translateService: TranslateService,
        private signalRService: SignalRService,
    ) {}

    get possibleFiltersList() {
        if (this.commonDataService.customerSettings.handleSettings.isEnabled) {
            return this.ticket.filteredRepliesCount;
        } else {
            return Object.keys(this.ticket.filteredRepliesCount)
                .filter((filterName) => {
                    return !['handledByMe', 'handled', 'unhandled'].includes(filterName);
                })
                .reduce((obj, filterName) => {
                    obj[filterName] = this.ticket.filteredRepliesCount[filterName];
                    return obj;
                }, {});
        }
    }

    ngOnInit() {
        this.initThreadFiltersList();

        this.signalRService
            .onFilteredRepliesCountUpdated()
            .pipe(takeUntil(this.unsubscriber))
            .subscribe((data: FilteredRepliesCountUpdatedEventModel) => {
                if (this.ticket?.id === data.ticketId) {
                    this.ticket.filteredRepliesCount = data.counts;
                    this.updateFilterCounts();
                }
            });
        this.searchQueryUpdate.pipe(debounceTime(700), takeUntil(this.unsubscriber)).subscribe(() => {
            this.appInsightsService.logEvent('SoMe filters used', {
                type: 'text search',
                community: this.commonDataService.communityName,
                value: this.threadFilterService.searchQuery,
            });
        });
    }

    cleanSearch() {
        this.threadFilterService.searchQuery = '';
    }

    initThreadFiltersList() {
        for (const filter in this.possibleFiltersList) {
            if (Object.prototype.hasOwnProperty.call(this.possibleFiltersList, filter)) {
                this.threadFilters.push({
                    name: this.threadFilterNamePipe.transform(this.threadFilterTypeEnum[filter]),
                    type: this.threadFilterTypeEnum[filter],
                    count: this.ticket.filteredRepliesCount[filter],
                    tooltipText: this.getTooltipText(this.threadFilterTypeEnum[filter]),
                });
            }
        }
    }

    removeFilter() {
        this.threadFilterService.activeThreadFilter = null;
    }

    toggleFilter(filter: ThreadFilterGuiModel) {
        if (this.threadFilterService.activeThreadFilter === filter) {
            this.removeFilter();
        } else {
            this.appInsightsService.logEvent('SoMe filters used', {
                type: filter.name,
                community: this.commonDataService.communityName,
            });
            this.threadFilterService.activeThreadFilter = filter;
        }
    }

    private getTooltipText(threadFilter: ThreadFilterType) {
        switch (threadFilter) {
            case ThreadFilterType.unansweredThreads:
                return this.translateService.instant('Messages which have no company reply');
            case ThreadFilterType.badWords:
                return this.translateService.instant('Messages which contain bad words');
            case ThreadFilterType.questions:
                return this.translateService.instant('Messages which include questions');
            case ThreadFilterType.essence:
                return this.translateService.instant(
                    'Exclude comments and comment replies that are shorter than 50 chars and only contains mentions and/or emojis',
                );
            // case ThreadFilterType.handledByMe:
            //     return this.translateService.instant('');
            // case ThreadFilterType.handled:
            //     return this.translateService.instant('');
            // case ThreadFilterType.unhandled:
            //     return this.translateService.instant('Unhandled');
        }
    }

    private updateFilterCounts() {
        this.threadFilters.forEach((filter) => {
            filter.count = this.ticket.filteredRepliesCount[this.threadFilterTypeEnum[filter.type]];
        });
    }

    ngOnDestroy() {
        this.unsubscriber.next();
        this.unsubscriber.complete();
    }
}
